import { Box, Text } from '@chakra-ui/react';
import { css as langCss } from '@codemirror/lang-css';
import { html as langHtml } from '@codemirror/lang-html';
import { javascript as langJavascript } from '@codemirror/lang-javascript';
import { sass as langSass } from '@codemirror/lang-sass';
import { indentUnit } from '@codemirror/language';
import { EditorState } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { abbreviationTracker } from '@emmetio/codemirror6-plugin';
import { githubLight } from '@uiw/codemirror-theme-github';
import CodeMirror from '@uiw/react-codemirror';
import { useCallback, useMemo, useRef, useState } from 'react';

import { createCssAutocomplete, createJsAutocomplete } from './CodeEditor/completions';
import { syntaxErrorLinter } from './CodeEditor/syntaxLinter';
import { IEditorSettings, TCodeTabName } from './types';
import useSandboxTheme from './useSandboxTheme';

interface IMultiPaneEditorProps {
  html: string;
  css: string;
  js: string;
  onHtmlChange: (value: string) => void;
  onCssChange: (value: string) => void;
  onJsChange: (value: string) => void;
  editorSettings: IEditorSettings;
  tabLabels: TCodeTabName[];
  orientation: 'horizontal' | 'vertical';
}

/**
 * Multi-pane code editor with resizable panes.
 * - Vertical orientation (left-right/right-left layouts): panes stacked vertically, labels on top act as resize handles
 * - Horizontal orientation (top-bottom layout): panes side-by-side with labels on left, explicit dividers between panes
 */
const MultiPaneEditor = ({
  html,
  css,
  js,
  onHtmlChange,
  onCssChange,
  onJsChange,
  editorSettings,
  tabLabels,
  orientation,
}: IMultiPaneEditorProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [paneSizes, setPaneSizes] = useState([33.33, 33.33, 33.34]);
  const [isDragging, setIsDragging] = useState(false);
  const rafRef = useRef<number | null>(null);

  const isVertical = orientation === 'vertical';

  const { resolved: resolvedTheme, colors } = useSandboxTheme();

  // Editor settings
  const { fontSize, lineNumbers, wordWrap, tabSize, framework } = editorSettings;
  const theme = resolvedTheme === 'light' ? githubLight : 'dark';

  // Font size theme
  const fontSizeTheme = useMemo(
    () =>
      EditorView.theme({
        '&': { fontSize: `${fontSize}px` },
        '.cm-gutters': { fontSize: `${fontSize}px` },
      }),
    [fontSize]
  );

  // Syntax error linter (Lezer parse errors → inline diagnostics)
  const linterExtension = useMemo(() => syntaxErrorLinter(), []);

  // Common extensions
  const commonExtensions = useMemo(() => {
    const extensions = [
      fontSizeTheme,
      EditorState.tabSize.of(tabSize),
      indentUnit.of(' '.repeat(tabSize)),
      linterExtension,
    ];
    if (wordWrap) {
      extensions.push(EditorView.lineWrapping);
    }
    return extensions;
  }, [fontSizeTheme, tabSize, wordWrap, linterExtension]);

  // Autocomplete extensions
  const jsAutocomplete = useMemo(() => createJsAutocomplete(framework), [framework]);
  const cssAutocomplete = useMemo(() => createCssAutocomplete(), []);

  // Handle resize for vertical orientation (label bar drag)
  const handleVerticalResize = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);

      let lastY = e.clientY;
      const currentSizes = [...paneSizes];

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
          if (!containerRef.current) return;

          const containerSize = containerRef.current.offsetHeight;
          const delta = moveEvent.clientY - lastY;
          const deltaPercent = (delta / containerSize) * 100;

          const minSize = 5; // Allow near-collapse
          const newSize = currentSizes[index] + deltaPercent;
          const nextSize = currentSizes[index + 1] - deltaPercent;

          if (newSize >= minSize && nextSize >= minSize) {
            currentSizes[index] = newSize;
            currentSizes[index + 1] = nextSize;
            setPaneSizes([...currentSizes]);
            lastY = moveEvent.clientY;
          }
        });
      };

      const handleMouseUp = () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        setIsDragging(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [paneSizes]
  );

  // Handle resize for horizontal orientation (divider drag)
  const handleHorizontalResize = useCallback(
    (dividerIndex: number, e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);

      let lastX = e.clientX;
      const currentSizes = [...paneSizes];

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
          if (!containerRef.current) return;

          const containerSize = containerRef.current.offsetWidth;
          const delta = moveEvent.clientX - lastX;
          const deltaPercent = (delta / containerSize) * 100;

          const minSize = 5; // Allow near-collapse
          const newSize = currentSizes[dividerIndex] + deltaPercent;
          const nextSize = currentSizes[dividerIndex + 1] - deltaPercent;

          if (newSize >= minSize && nextSize >= minSize) {
            currentSizes[dividerIndex] = newSize;
            currentSizes[dividerIndex + 1] = nextSize;
            setPaneSizes([...currentSizes]);
            lastX = moveEvent.clientX;
          }
        });
      };

      const handleMouseUp = () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        setIsDragging(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [paneSizes]
  );

  // Render a vertical pane (for left-right/right-left layouts)
  const renderVerticalPane = (
    type: 'html' | 'css' | 'js',
    value: string,
    onChange: (value: string) => void,
    label: TCodeTabName,
    size: number,
    extensions: any[],
    index: number
  ) => {
    const isFirst = index === 0;
    const canDrag = !isFirst;

    return (
      <Box
        key={type}
        display="flex"
        flexDirection="column"
        flex={`0 0 ${size}%`}
        minHeight={0}
        overflow="hidden"
        bg={colors.editorChromeBg}
        transition={isDragging ? 'none' : 'flex 0.1s ease-out'}
      >
        {/* Horizontal label bar on top - acts as resize handle */}
        <Box
          height="43px"
          width="100%"
          bg="transparent"
          borderBottom="1px solid"
          borderBottomColor={colors.editorBorder}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          px={4}
          cursor={canDrag ? 'row-resize' : 'default'}
          onMouseDown={canDrag ? (e) => handleVerticalResize(index - 1, e) : undefined}
          flexShrink={0}
          _hover={canDrag ? { bg: colors.hoverBg } : undefined}
        >
          <Text
            fontSize="small"
            fontWeight="medium"
            letterSpacing="0.02em"
            textTransform="uppercase"
            color={colors.textPrimary}
            userSelect="none"
          >
            {label}
          </Text>
        </Box>

        {/* Code editor area */}
        <Box flex={1} overflow="hidden" minHeight={0}>
          <CodeMirror
            value={value}
            onChange={onChange}
            extensions={extensions}
            theme={theme}
            basicSetup={{ foldGutter: false, lineNumbers }}
            style={{ height: '100%' }}
          />
        </Box>
      </Box>
    );
  };

  // Render a horizontal pane (for top-bottom layout)
  // Label bars act as resize handles (same pattern as vertical orientation)
  const renderHorizontalPane = (
    type: 'html' | 'css' | 'js',
    value: string,
    onChange: (value: string) => void,
    label: TCodeTabName,
    size: number,
    extensions: any[],
    index: number
  ) => {
    const isFirst = index === 0;
    const canDrag = !isFirst;

    return (
      <Box
        key={type}
        display="flex"
        flexDirection="row"
        width={`${size}%`}
        height="100%"
        overflow="hidden"
        bg={colors.editorChromeBg}
        transition={isDragging ? 'none' : 'width 0.1s ease-out'}
      >
        {/* Vertical label bar on left - acts as resize handle */}
        <Box
          width="44px"
          height="100%"
          bg="transparent"
          borderRight="1px solid"
          borderRightColor={colors.editorBorder}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          cursor={canDrag ? 'col-resize' : 'default'}
          onMouseDown={canDrag ? (e) => handleHorizontalResize(index - 1, e) : undefined}
          _hover={canDrag ? { bg: colors.hoverBg } : undefined}
        >
          <Text
            fontSize="small"
            fontWeight="medium"
            letterSpacing="0.02em"
            textTransform="uppercase"
            color={colors.textPrimary}
            userSelect="none"
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
            }}
          >
            {label}
          </Text>
        </Box>

        {/* Code editor area */}
        <Box flex={1} overflow="hidden" minWidth={0}>
          <CodeMirror
            value={value}
            onChange={onChange}
            extensions={extensions}
            theme={theme}
            basicSetup={{ foldGutter: false, lineNumbers }}
            style={{ height: '100%' }}
          />
        </Box>
      </Box>
    );
  };

  // Build the list of panes to render based on tabLabels
  const allPanes = [
    {
      type: 'html' as const,
      value: html,
      onChange: onHtmlChange,
      extensions: [langHtml({ selfClosingTags: true, autoCloseTags: false }), abbreviationTracker(), ...commonExtensions],
    },
    {
      type: 'css' as const,
      value: css,
      onChange: onCssChange,
      extensions: [editorSettings.cssMode === 'scss' ? langSass() : langCss(), cssAutocomplete, ...commonExtensions],
    },
    {
      type: 'js' as const,
      value: js,
      onChange: onJsChange,
      extensions: [langJavascript({ jsx: true, typescript: true }), jsAutocomplete, ...commonExtensions],
    },
  ];

  // Map tabLabels to the corresponding panes
  // e.g. ['JS'] → only JS pane, ['HTML', 'CSS', 'JS'] → all three
  const activePanes = tabLabels.map((label) => {
    const upper = label.toUpperCase();
    let type: 'html' | 'css' | 'js';
    if (upper === 'HTML') type = 'html';
    else if (upper.startsWith('CSS') || upper === 'SCSS' || upper === 'POSTCSS') type = 'css';
    else type = 'js';
    const pane = allPanes.find((p) => p.type === type)!;
    return { ...pane, label };
  });

  // Equal sizing for active panes
  const equalSize = 100 / activePanes.length;

  // Full-screen overlay during drag to capture mouse events everywhere
  const dragOverlay = isDragging ? (
    <Box
      position="fixed"
      inset={0}
      zIndex={9999}
      cursor={isVertical ? 'row-resize' : 'col-resize'}
    />
  ) : null;

  // Vertical orientation: panes stacked, labels on top act as resize handles
  if (isVertical) {
    return (
      <Box
        ref={containerRef}
        display="flex"
        flexDirection="column"
        height="100%"
        width="100%"
        overflow="hidden"
        bg={colors.editorBg}
      >
        {dragOverlay}
        {activePanes.map((pane, index) =>
          renderVerticalPane(
            pane.type,
            pane.value,
            pane.onChange,
            pane.label,
            activePanes.length === 1 ? 100 : (paneSizes[index] ?? equalSize),
            pane.extensions,
            index
          )
        )}
      </Box>
    );
  }

  // Horizontal orientation: panes side-by-side, label bars act as resize handles
  return (
    <Box
      ref={containerRef}
      display="flex"
      flexDirection="row"
      height="100%"
      width="100%"
      overflow="hidden"
      bg={colors.editorBg}
    >
      {dragOverlay}
      {activePanes.map((pane, index) =>
        renderHorizontalPane(
          pane.type,
          pane.value,
          pane.onChange,
          pane.label,
          activePanes.length === 1 ? 100 : (paneSizes[index] ?? equalSize),
          pane.extensions,
          index
        )
      )}
    </Box>
  );
};

export default MultiPaneEditor;
